import Rails from "@rails/ujs"
import { Controller } from "@hotwired/stimulus"

import loadStripeScript from "../app/components/stripe-script-loader"

export default class extends Controller {
  static targets = [ "partialPaymentMode", "partialPaymentAmount",
                     "cardElement", "addressElement",
                     "cardErrors", "loadErrors"]

  initialize() {
    // needed before init() so __loadError() can still disable the form
    // when the Stripe script never arrives and init() is never called
    this.$form = $(this.element)
    this.isSubmitting = false

    loadStripeScript().
      then(() => this.init()).
      catch(() => this.__loadError())
  }

  connect() {
    // an attempt rejected by our server (see pay.js.erb) releases the lock so the customer can retry.
    // deliberately not payment:idle - we dispatch that one ourselves on widget updates
    // and must not let it unlock a submit that is still in flight
    this.releaseSubmitLock = () => {
      this.isSubmitting = false
      this.readyToSubmit = false
    }

    document.addEventListener('payment:rejected', this.releaseSubmitLock)
  }

  disconnect() {
    document.removeEventListener('payment:rejected', this.releaseSubmitLock)
  }

  get isPartialPaymentMode() {
    return this.hasPartialPaymentModeTarget && this.partialPaymentModeTargets.find(o => o.checked).value == "on"
  }

  get partialPaymentAmount() {
    let partialAmount = 0

    if( this.hasPartialPaymentAmountTarget ) partialAmount = parseFloat(this.partialPaymentAmountTarget.value.replaceAll(',', ''))
    if( isNaN(partialAmount) ) partialAmount = 0

    partialAmount = Math.trunc(partialAmount * 100)

    return partialAmount
  }

  get isApplyPartialPaymentAmount() {
    let partialAmount = this.partialPaymentAmount

    return this.isPartialPaymentMode &&
      partialAmount != this.previousPartialAmount &&
      partialAmount > 0 && partialAmount < this.data.get('total')
  }

  init() {
    try {
      var self = this

      this.previousPartialAmount = 0

      this.stripe = Stripe(this.data.get('key'))

      let returnUrl = this.data.get('return-url')
      let options = {
        clientSecret: this.data.get('intent-key'),
        appearance: {
          theme: 'stripe',
          variables: {
            colorTextPlaceholder: '#B3BCC7',
            colorText: '#071E3E'
          }
        }
      }

      this.stripeElements = this.stripe.elements(options)

      let paymentOptions = {
        paymentMethodOrder: ['card', 'apple_pay', 'google_pay']
      }
      let accountName = this.data.get('account-name')

      if( accountName ) {
        paymentOptions.business = {
          name: accountName
        }
      }

      this.card = this.stripeElements.create('payment', paymentOptions)
      this.card.mount(this.cardElementTarget)

      if(this.hasAddressElementTarget) {
        let addressOptions = { mode: 'billing' }
        this.address = this.stripeElements.create('address', addressOptions)
        this.address.mount(this.addressElementTarget)
      } else {
        this.address = null
      }

      // this flag will prevent updating PaymentIntent on initial rendering
      // when the first "change" event fired without actual changing
      this.paymentWidgetReady = false
      // it prevent updating PaymentIntent on every widget change
      // even when the payment type stays the same
      this.previousPaymentType = 'card'

      this.card.on('ready', (event)=> {
        this.paymentWidgetReady = true
        // a mounted element is not a usable one, only "ready" means the widget can take input
        this.dispatch('ready', { prefix: 'payment', target: document })
      })
      // listen for widget changes and update the PaymentIntent to recalculate fees depends on chosen payment type
      this.card.on('change', (event)=> {
        if( event.elementType == 'payment' && this.paymentWidgetReady && this.previousPaymentType != event.value.type ) {
          this.previousPaymentType = event.value.type

          // enable billing address on card payments only
          if(this.address) {
            if(event.value.type == 'card') {
              this.address.mount(this.addressElementTarget)
            } else {
              this.address.unmount()
            }
          }

          this.__disablePayButton()

          let url = this.data.get('intent-update-url')

          let updateData = {
            "payment_method": event.value.type
          }

          if( this.isApplyPartialPaymentAmount ) {
            updateData.payment_amount = this.partialPaymentAmount
          }

          $.ajax(url, {
            type: 'PATCH',
            dataType: 'json',
            data: updateData
          })
            .done((response) => {
              this.stripeElements.fetchUpdates().
                then((response) => {
                  this.__enablePayButton()
                })
            })
            .fail(() => this.__enablePayButton())
        }
      })

      this.$form.on("form:readyToSubmit", (event) => {
        this.readyToSubmit = true
        this.internalSubmit = true
        this.$form.submit()
      })

      this.$form.submit(function(event) {
        // our own re-submit after the PaymentIntent amount was updated, let it through
        if(self.internalSubmit) {
          self.internalSubmit = false
        } else if(self.isSubmitting) {
          // returning false here stops propagation, so rails-ujs never sees this submit
          // and data-disable-with can't help us, the lock is the only guard
          return false
        }

        // only hold the lock when something was actually started,
        // otherwise a no-op submit would leave the button stuck
        if( self.isApplyPartialPaymentAmount ) {
          self.isSubmitting = true
          self.__updatePaymentIntentAmount()
        } else {
          self.isSubmitting = self.__submitForm()
        }

        return self.readyToSubmit
      })

    } catch(e) {
      this.__loadError()
    }
  }

  __updatePaymentIntentAmount() {
    this.readyToSubmit = false
    this.previousPartialAmount = this.partialPaymentAmount
    this.__disablePayButton()

    let url = this.data.get('intent-update-url')

    $.ajax(url, {
      type: 'PATCH',
      dataType: 'json',
      data: {
        "payment_amount": this.partialPaymentAmount,
      }})
      .done((response) => {
        this.stripeElements.fetchUpdates().
          then((response) => {
            // release the disable from above before re-submitting, the submit path
            // sets its own busy state and must be the last one to touch the button
            this.__enablePayButton()
            this.$form.trigger("form:readyToSubmit")
          })
      })
      .fail(() => this.__attemptFailed())
  }

  // returns whether a payment attempt was actually started
  __submitForm() {
    let attemptStarted = false

    if($(this.cardElementTarget).is(':visible')) {
      attemptStarted = true
      this.__disablePayButton()

      let returnUrl = this.data.get('return-url')
      let submitOptions = {
        elements: this.stripeElements,
        confirmParams: {
          return_url: returnUrl
        }
      }

      // clear the possible error mesage
      this.cardErrorsTarget.innerText = ""

      var self = this

      this.stripe.confirmPayment(submitOptions).then(function(result) {
        // no error means Stripe is taking the customer to the return_url,
        // re-enabling here would hand back a live Pay button on top of a payment already in flight
        if(!result.error) return

        let errorMessage = result.error.message
        if((/^missing.*?address/i).test(errorMessage)) {
          errorMessage = "Address is required for this payment type"
        }

        // Inform the user if there was an error.
        self.cardErrorsTarget.innerText = errorMessage
        self.__attemptFailed()
      })
    }

    // if the card_choice visible and set to "saved card" submit payment form as usual
    // to process PI on our side with saved payment method, instead of stripe processing
    if(this.$form.find("[name='paylink_payment_form[card_choice]']").is(':visible') &&
       this.$form.find("[name='paylink_payment_form[card_choice]']:checked").val() == 'saved') {
      let hiddenInput = document.createElement('input')
      hiddenInput.setAttribute('type', 'hidden')
      hiddenInput.setAttribute('name', 'intent_id')
      hiddenInput.setAttribute('value', this.data.get('intent-id'))

      this.$form.append(hiddenInput)

      this.readyToSubmit = true
      attemptStarted = true
    } else {
      // do not submit form to server, because payment will be processed by the Stripe
      this.readyToSubmit = false
    }

    return attemptStarted
  }

  toggleSaveCard(event) {
    this.__disablePayButton()

    let url = this.data.get('intent-update-url')

    $.ajax(url, {
      type: 'PATCH',
      dataType: 'json',
      data: {
        "save_card": event.target.checked,
      }})
    .done((response) => {
      this.stripeElements.fetchUpdates().
        then((response) => {
          this.__enablePayButton()
        })
    })
    .fail(() => this.__enablePayButton())
  }

  // the attempt is over and the customer may try again
  __attemptFailed() {
    this.readyToSubmit = false
    this.isSubmitting = false
    this.__enablePayButton()
  }

  __disablePayButton() {
    this.dispatch('busy', { prefix: 'payment', target: document })
  }

  __enablePayButton() {
    this.dispatch('idle', { prefix: 'payment', target: document })
  }

  // Handle real-time validation errors from the stripe elements
  __errorsHandler(event) {
    if(event.error) {
      this.cardErrorsTarget.innerText = event.error.message
    } else {
      this.cardErrorsTarget.innerText = ''
    }
  }

  __loadError() {
    if(this.hasLoadErrorsTarget) $(this.loadErrorsTarget).removeClass('d-none')
    this.$form.submit(() => false)
    this.dispatch('failed', { prefix: 'payment', target: document })
  }
}
